import * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import {
  GSPLAT_BATCH_INDEX_COUNT,
  GSPLAT_BATCH_VERTEX_COUNT,
  GSPLAT_INSTANCE_SIZE,
} from '@/plugins/gsplat/core/gsplatBatching'
import type {
  GSplatBindingInvalidationReason,
  GSplatBindingRuntime,
  GSplatBindingTransaction,
  GSplatHandleBehavior,
  GSplatHandleBindingCreateInput,
  IGSplatHandleBinding,
  IGSplatHandleBindingFactory,
} from '@/plugins/gsplat/GSplatHandleBinding'
import {
  type GSplatHandleBinding,
  GSplatHandleBindingFactory,
} from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GSplatExportSource,
  GSplatGpuPickCoordinator,
  GSplatPickSample,
  GSplatSystemAssetSnapshot,
  GSplatSystemConfiguration,
  GSplatSystemDirector,
  GSplatSystemHostServices,
  GSplatSystemRenderSubmission,
  GSplatViewLifecycleContext,
  GSplatViewRetireReason,
  SplatDataCpuPicker as SplatDataCpuPickerContract,
} from '@/plugins/gsplat/GSplatSystem'
import { GSplatSystem } from '@/plugins/gsplat/GSplatSystem'
import type { GSplatVisualActivityCallback } from '@/plugins/gsplat/GSplatSystemFactory'
import type {
  GSplatBufferLeaf,
  GSplatDrawObjectLeaf,
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
  GSplatPickTargetLease,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import {
  WebGLGSplatGraphicsAdapter,
  WebGLGSplatWorkBufferPassFactory,
} from '@/plugins/gsplat/graphics/WebGLGSplatGraphicsAdapter'
import { createCanonicalGSplatLoaderRegistry } from '@/plugins/gsplat/loaders/GSplatCanonicalLoaders'
import type { GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { Layer } from '@/plugins/gsplat/lod/Layer'
import { LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import { GSplatUnifiedSorter } from '@/plugins/gsplat/sorting/GSplatUnifiedSorter'
import {
  SplatDataCpuPicker,
  type SplatDataCpuPickSource,
} from '@/plugins/gsplat/spatial/SplatDataCpuPicker'
import { GSplatParamsRuntimeControl } from '@/plugins/gsplat/system/GSplatParamsRuntimeControl'
import { GSplatUnifiedShaderEffectController } from '@/plugins/gsplat/system/GSplatUnifiedShaderEffectController'
import type { UnifiedScalarHistogramSource } from '@/plugins/gsplat/system/unifiedScalarHistogram'
import { computeUnifiedScalarHistogram } from '@/plugins/gsplat/system/unifiedScalarHistogram'
import { DEFAULT_LOD_CONFIG } from '@/plugins/gsplat/types/ConfigTypes'
import type {
  GaussianSplatPluginOptions,
  GaussianSplatRenderConfig,
} from '@/plugins/gsplat/types/PluginTypes'
import type {
  GSplatDirectorManagerFactory,
  GSplatDirectorView,
} from '@/plugins/gsplat/unified/GSplatDirector'
import { GSplatDirector } from '@/plugins/gsplat/unified/GSplatDirector'
import {
  type GSplatHybridPipelineFactory,
  GSplatHybridRenderer,
} from '@/plugins/gsplat/unified/GSplatHybridRenderer'
import { GSplatHybridRendererScratch } from '@/plugins/gsplat/unified/GSplatHybridRendererScratch'
import type {
  GSplatManagerFrameRuntime,
  GSplatManagerWorld,
  GSplatRendererFactory,
} from '@/plugins/gsplat/unified/GSplatManager'
import { GSplatManager } from '@/plugins/gsplat/unified/GSplatManager'
import { GSplatParams } from '@/plugins/gsplat/unified/GSplatParams'
import { GSPLAT_PICK_CLEAR_ID, GSplatPickIndex } from '@/plugins/gsplat/unified/GSplatPickIndex'
import type { GSplatParameter, GSplatPlacement } from '@/plugins/gsplat/unified/GSplatPlacement'
import { GSplatQuadRenderer } from '@/plugins/gsplat/unified/GSplatQuadRenderer'
import type {
  GSplatRenderer,
  GSplatRenderViewParams,
} from '@/plugins/gsplat/unified/GSplatRenderer'
import { GSplatResourceCleanup } from '@/plugins/gsplat/unified/resource/GSplatResourceCleanup'
import type { GSplatWorkBufferPassFactory } from '@/plugins/gsplat/unified/world/GSplatWorkBuffer'
import { GSplatWorld } from '@/plugins/gsplat/unified/world/GSplatWorld'
import type {
  GSplatWorldSplat,
  GSplatWorldState,
} from '@/plugins/gsplat/unified/world/GSplatWorldState'
import { resolveGSplatDeviceBudgetConfig } from '@/plugins/gsplat/utils/deviceBudget'
import type { EngineContext } from '@/runtime/EngineContext'
import { getLoggerManager } from '@/shared/logging'
import type { AssetExporter, PhaseRenderContext } from '@/shared/types/assets'
import type { AssetId, ClipVolume, PickQuery, RectPickQuery } from '@/shared/types/core'
import type {
  CentersDisplayMode,
  ColorAdjustmentOptions,
  ColorAdjustmentResult,
  GSplatDeviceBudgetDiagnostics,
  GSplatVisualActivitySnapshot,
  LODConfig,
  LODStats,
} from '@/shared/types/gsplat'
import type {
  GSplatCapability,
  GSplatRelightingCapability,
  GSplatRelightingTargetFormat,
} from '@/shared/types/gsplatCapability'
import type { EngineFrameContext, ViewFrameContext, ViewId } from '@/shared/types/view'
import { ndcToRay } from '@/shared/utils'
import { isPickPointAtOrBeyondMinDistance } from '@/shared/utils/pickDepth'

const log = getLoggerManager().getLogger('gsplat:picking')

const DEFAULT_CONFIG: GaussianSplatRenderConfig = Object.freeze({
  sortingMode: 'auto',
  qualityLevel: 'medium',
  maxSplats: 0,
  opacity: 1,
})

const EMPTY_LOD_STATS: LODStats = Object.freeze({
  totalGroups: 0,
  visibleNodes: 0,
  loadedNodes: 0,
  cachedPoints: 0,
  pendingLoads: 0,
  activeLoads: 0,
  cachedFiles: 0,
})

export const WEBGL_UNIFIED_GSPLAT_CAPABILITIES: GSplatCapability = Object.freeze({
  supported: Object.freeze([
    'unified-format-reader',
    'uncompressed-reader',
    'compressed-reader',
    'sog-reader',
    'lod-streaming',
    'multi-view',
    'cpu-sort',
    'cpu-picking',
    'gpu-picking',
    'shader-effects',
    'work-buffer-modifier',
    'relighting',
  ] as const),
  gpuCompute: Object.freeze({
    compute: false,
    storageBuffers: false,
    storageTextures: false,
    atomicUint32: false,
    indirectDispatch: false,
    indirectDraw: false,
    maxStorageBufferBindingSize: 0,
    maxBufferSize: 0,
    maxStorageBuffersPerShaderStage: 0,
    maxComputeWorkgroupStorageSize: 0,
    maxComputeInvocationsPerWorkgroup: 0,
    maxComputeWorkgroupSizeX: 0,
    maxComputeWorkgroupsPerDimension: 0,
    radixScratchBytes: 0,
  }),
  gpuPicking: Object.freeze({
    privateTarget: true,
    targetFormats: Object.freeze(['rgba8unorm'] as const),
    asyncReadback: true,
    maxPickIds: 0x00ff_ffff,
  }),
  relighting: Object.freeze({
    privateTarget: true,
    targetFormats: Object.freeze(['rgba8unorm'] as const),
    floatFilterable: false,
    unormFallback: true,
  }),
})

/**
 * Relighting target 能力必须来自当前 adapter 的实际 format mapping。
 *
 * WebGL 的静态 capability 仍保留给兼容调用方，但 Unified system 使用该工厂，避免
 * 在 adapter 不能线性过滤目标时把“可创建 target”误报成可用的 relighting pass。
 */
export function createGSplatRelightingCapability(
  adapter: GSplatGraphicsAdapter
): GSplatRelightingCapability {
  const floatInfo = adapter.getTextureFormatInfo('rgba16float')
  const unormInfo = adapter.getTextureFormatInfo('rgba8unorm')
  const floatFilterable = floatInfo.renderable && floatInfo.filterable
  const unormFallback = unormInfo.renderable && unormInfo.filterable
  const targetFormats: GSplatRelightingTargetFormat[] = []
  if (floatFilterable) targetFormats.push('rgba16float')
  if (unormFallback) targetFormats.push('rgba8unorm')
  return Object.freeze({
    privateTarget: targetFormats.length > 0,
    targetFormats: Object.freeze(targetFormats),
    floatFilterable,
    unormFallback,
  })
}

export function createWebGLGSplatCapabilities(adapter: GSplatGraphicsAdapter): GSplatCapability {
  return Object.freeze({
    ...WEBGL_UNIFIED_GSPLAT_CAPABILITIES,
    relighting: createGSplatRelightingCapability(adapter),
  })
}

export function createWebGPUGSplatCapabilities(adapter: GSplatGraphicsAdapter): GSplatCapability {
  return Object.freeze({
    supported: Object.freeze([
      'unified-format-reader',
      'uncompressed-reader',
      'compressed-reader',
      'sog-reader',
      'lod-streaming',
      'multi-view',
      'cpu-sort',
      'gpu-sort',
      'cpu-picking',
      'gpu-picking',
      'shader-effects',
      'work-buffer-modifier',
      'relighting',
    ] as const),
    gpuCompute: Object.freeze({
      compute: true,
      storageBuffers: true,
      storageTextures: true,
      atomicUint32: true,
      indirectDispatch: true,
      indirectDraw: true,
      maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      maxBufferSize: adapter.limits.maxBufferSize,
      maxStorageBuffersPerShaderStage: adapter.limits.maxStorageBuffersPerShaderStage,
      maxComputeWorkgroupStorageSize: adapter.limits.maxComputeWorkgroupStorageSize,
      maxComputeInvocationsPerWorkgroup: adapter.limits.maxComputeInvocationsPerWorkgroup,
      maxComputeWorkgroupSizeX: adapter.limits.maxComputeWorkgroupSizeX,
      maxComputeWorkgroupsPerDimension: adapter.limits.maxComputeWorkgroupsPerDimension,
      radixScratchBytes: Math.min(
        adapter.limits.maxBufferSize,
        adapter.limits.maxStorageBufferBindingSize
      ),
    }),
    gpuPicking: Object.freeze({
      privateTarget: true,
      targetFormats: Object.freeze(['rgba8unorm'] as const),
      asyncReadback: true,
      maxPickIds: 0x00ff_ffff,
    }),
    relighting: createGSplatRelightingCapability(adapter),
  })
}

export interface WebGLGSplatUnifiedCompositionInput {
  readonly context: EngineContext
  readonly adapter: WebGLGSplatGraphicsAdapter
  readonly options: Readonly<GaussianSplatPluginOptions>
  readonly initialConfig?: Partial<GaussianSplatRenderConfig>
  readonly exporters?: readonly AssetExporter<GSplatExportSource, Blob>[]
  readonly onVisualActivity: GSplatVisualActivityCallback
}

type UnifiedGSplatGraphicsAdapter = GSplatGraphicsAdapter & {
  getDrawObject(draw: GSplatDrawObjectLeaf): THREE.Mesh
}

export interface UnifiedGSplatCompositionInput {
  readonly context: EngineContext
  readonly adapter: UnifiedGSplatGraphicsAdapter
  readonly workBufferPassFactory: GSplatWorkBufferPassFactory
  readonly capabilities: GSplatCapability
  readonly renderWebGPUScene?: (scene: THREE.Scene, camera: THREE.Camera) => void
  readonly createHybridPipelineFactory?: (
    scratch: GSplatHybridRendererScratch
  ) => GSplatHybridPipelineFactory
  readonly createGpuPickCoordinator?: (
    input: UnifiedGSplatGpuPickCoordinatorInput
  ) => GSplatGpuPickCoordinator
  readonly options: Readonly<GaussianSplatPluginOptions>
  readonly initialConfig?: Partial<GaussianSplatRenderConfig>
  readonly exporters?: readonly AssetExporter<GSplatExportSource, Blob>[]
  readonly onVisualActivity: GSplatVisualActivityCallback
}

export interface UnifiedGSplatGpuPickCoordinatorInput {
  readonly adapter: UnifiedGSplatGraphicsAdapter
  readonly scene: THREE.Scene
  readonly director: GSplatDirector<GSplatPlacement, GSplatDrawObjectLeaf>
  readonly layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>
  readonly placementAssets: ReadonlyMap<number, PlacementAssetEntry>
  readonly viewFrames: ReadonlyMap<ViewId, ViewFrameContext>
  readonly onVisualActivity: GSplatVisualActivityCallback
}

interface DrawEntry {
  readonly viewId: ViewId
  readonly draw: GSplatDrawObjectLeaf
}

export class UnifiedGSplatConfiguration implements GSplatSystemConfiguration {
  private config: GaussianSplatRenderConfig
  private colorAdjustmentValue: ColorAdjustmentResult = {
    brightness: 0,
    temperature: 0,
    contrast: 1,
    saturation: 1,
    opacity: 1,
  }

  constructor(
    readonly capabilities: GSplatCapability,
    private readonly params: GSplatParams,
    private readonly readManagers: () => readonly GSplatManager[],
    private readonly onVisualActivity: (reason: string) => void,
    initialConfig: Partial<GaussianSplatRenderConfig> = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...initialConfig }
    validateRenderConfig(this.config)
    this.assertWebGpuConfig(this.config)
    this.params.setRendererSelection(this.config.sortingMode, [])
    this.params.material.setParameter('colorOpacity', this.config.opacity)
    this.colorAdjustmentValue.opacity = this.config.opacity
  }

  getConfig(): GaussianSplatRenderConfig {
    return { ...this.config }
  }

  getResolvedSortingMode(): 'cpu' | 'gpu' {
    return this.params.currentRenderer
  }

  setConfig(config: Partial<GaussianSplatRenderConfig>): void {
    const next = { ...this.config, ...config }
    validateRenderConfig(next)
    this.assertWebGpuConfig(next)
    if (next.sortingMode !== this.config.sortingMode) {
      this.params.setRendererSelection(next.sortingMode, this.readManagers())
    }
    if (next.opacity !== this.config.opacity) {
      this.params.material.setParameter('colorOpacity', next.opacity)
      this.colorAdjustmentValue.opacity = next.opacity
    }
    if (next.maxSplats !== this.config.maxSplats) {
      this.params.splatBudget = Math.min(this.params.splatBudget || next.maxSplats, next.maxSplats)
    }
    this.config = next
    this.onVisualActivity('gsplat.config')
  }

  setColorAdjustment(options: ColorAdjustmentOptions): boolean {
    const next = { ...this.colorAdjustmentValue }
    for (const [key, value] of Object.entries(options) as Array<
      [keyof ColorAdjustmentResult, number | undefined]
    >) {
      if (value === undefined) continue
      if (!Number.isFinite(value)) throw new Error(`非法 GSplat color adjustment ${key}: ${value}`)
      if (key === 'opacity' && (value < 0 || value > 1)) {
        throw new Error(`非法 GSplat opacity: ${value}`)
      }
      next[key] = value
    }
    const changed = Object.keys(next).some(
      (key) =>
        next[key as keyof ColorAdjustmentResult] !==
        this.colorAdjustmentValue[key as keyof ColorAdjustmentResult]
    )
    if (!changed) return false
    this.colorAdjustmentValue = next
    for (const [name, value] of Object.entries(next)) {
      this.params.material.setParameter(`color${name[0].toUpperCase()}${name.slice(1)}`, value)
    }
    if (next.opacity !== this.config.opacity)
      this.config = { ...this.config, opacity: next.opacity }
    this.onVisualActivity('gsplat.colorAdjustment')
    return true
  }

  getColorAdjustment(): ColorAdjustmentResult {
    return { ...this.colorAdjustmentValue }
  }

  resetColorAdjustment(): boolean {
    return this.setColorAdjustment({
      brightness: 0,
      temperature: 0,
      contrast: 1,
      saturation: 1,
      opacity: 1,
    })
  }

  private assertWebGpuConfig(config: GaussianSplatRenderConfig): void {
    if (this.params.adapter.backend === 'webgpu' && config.qualityLevel !== 'medium') {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-quality-level',
        this.params.adapter.backend,
        `仅支持默认 qualityLevel=medium，收到 ${config.qualityLevel}`
      )
    }
  }
}

class UnifiedBindingRuntime implements GSplatBindingRuntime {
  constructor(
    private readonly composition: LayerComposition<GSplatPlacement, GSplatDrawObjectLeaf>,
    private readonly onVisualActivity: (reason: string) => void
  ) {}

  resolveParameter(name: string, data: unknown): GSplatParameter {
    return Object.freeze({ scopeId: name, data })
  }

  invalidatePlacement(placement: GSplatPlacement, reason: GSplatBindingInvalidationReason): void {
    placement.markDirty()
    if (reason === 'membership') {
      for (const layer of this.composition.layerList) layer.gsplatPlacementsDirty = true
      this.onVisualActivity(`gsplat.${reason}`)
      return
    }
    for (const layer of this.composition.layerList) {
      if (layer.gsplatPlacementsSet.has(placement) || layer.gsplatShadowCastersSet.has(placement)) {
        layer.gsplatPlacementsDirty = true
      }
    }
    this.onVisualActivity(`gsplat.${reason}`)
  }

  stagePlacementReplacement(): GSplatBindingTransaction {
    return noopBindingTransaction()
  }

  stageWorkBufferModifier(): GSplatBindingTransaction {
    return noopBindingTransaction()
  }
}

export interface PlacementAssetEntry {
  readonly id: AssetId
  readonly binding: GSplatHandleBinding
}

/**
 * 统一 scalar 行为：设置/清除 placement.scalarFilter 并触发 WorkBuffer 重拷，
 * 直方图直接使用 Resource 保留的格式原生 CPU 数据（volume / surface-area / value），
 * 与 copy pass 的渲染过滤共用同一标量定义。
 */
export function createUnifiedScalarFilterBehavior(
  assetId: AssetId,
  resolvePlacement: () => GSplatPlacement | null,
  invalidatePlacement: (placement: GSplatPlacement) => void,
  resolveHistogramSource: () => UnifiedScalarHistogramSource | null
): Required<
  Pick<GSplatHandleBehavior, 'setScalarFilter' | 'clearScalarFilter' | 'getScalarHistogram'>
> {
  return {
    setScalarFilter: (options) => {
      const placement = resolvePlacement()
      if (!placement) return false
      if (!options.enabled) {
        placement.scalarFilter = null
      } else {
        if (!Number.isFinite(options.min) || !Number.isFinite(options.max)) return false
        if (options.min > options.max) return false
        if (
          options.type !== 'volume' &&
          options.type !== 'surface-area' &&
          options.type !== 'value'
        ) {
          return false
        }
        placement.scalarFilter = {
          type: options.type,
          min: options.min,
          max: options.max,
        }
      }
      invalidatePlacement(placement)
      return true
    },
    clearScalarFilter: () => {
      const placement = resolvePlacement()
      if (!placement) return false
      placement.scalarFilter = null
      invalidatePlacement(placement)
      return true
    },
    getScalarHistogram: async (options) => {
      const source = resolveHistogramSource()
      if (!source) return null
      return computeUnifiedScalarHistogram({ source, assetId, options })
    },
  }
}

export class TrackingBindingFactory implements IGSplatHandleBindingFactory {
  readonly placementAssets = new Map<number, PlacementAssetEntry>()
  private readonly delegate: GSplatHandleBindingFactory

  constructor(options: ConstructorParameters<typeof GSplatHandleBindingFactory>[0]) {
    this.delegate = new GSplatHandleBindingFactory({
      ...options,
      onDispose: (binding) => {
        const placementId = binding.placement.id
        if (this.placementAssets.get(placementId)?.binding === binding) {
          this.placementAssets.delete(placementId)
        }
        options.onDispose?.(binding)
      },
    })
  }

  create(input: GSplatHandleBindingCreateInput): IGSplatHandleBinding {
    const binding = this.delegate.create(input)
    this.placementAssets.set(binding.placement.id, { id: input.id, binding })
    return binding
  }
}

class UnifiedViewFrameRuntime implements GSplatManagerFrameRuntime {
  readonly logicalFrameSource: object
  readonly camera: THREE.Camera
  logicalFrameToken = 0
  private frame: ViewFrameContext | null

  constructor(
    camera: THREE.Camera,
    frame: ViewFrameContext | undefined,
    private readonly viewId: ViewId,
    private readonly params: GSplatParams,
    logicalFrameSource: object,
    private readonly readExposure: () => number | undefined,
    private readonly readSceneFog: () => unknown,
    private readonly onVisualActivity: (reason: string, viewId?: ViewId) => void,
    private readonly readShaderEffectOwner: () => AssetId | null
  ) {
    this.camera = camera
    this.frame = frame ?? null
    this.logicalFrameToken = frame?.frameNumber ?? 0
    this.logicalFrameSource = logicalFrameSource
  }

  get shaderEffectOwner(): AssetId | null {
    return this.readShaderEffectOwner()
  }

  get radialSorting(): boolean {
    return this.params.radialSorting
  }

  get cooldownTicks(): number {
    return this.params.cooldownTicks
  }

  get paramsDirty(): boolean {
    return this.params.dirty
  }

  get exposure(): number | undefined {
    return this.readExposure()
  }

  get useFog(): boolean {
    return this.params.useFog
  }

  get cameraFogParams(): unknown {
    return undefined
  }

  get sceneFog(): unknown {
    return this.readSceneFog()
  }

  updateFrame(frame: ViewFrameContext): void {
    this.frame = frame
    this.logicalFrameToken = frame.frameNumber
  }

  advanceFrame(frame: EngineFrameContext): void {
    this.logicalFrameToken = frame.frameNumber
  }

  writeRenderViewParams(target: GSplatRenderViewParams): void {
    const frame = this.frame
    this.params.syncMaterialTemplate()
    Object.assign(target, {
      cameraNode: this.camera,
      radialSorting: this.params.radialSorting,
      alphaClip: this.params.alphaClip,
      alphaClipForward: this.params.alphaClipForward,
      minPixelSize: this.params.minPixelSize,
      minContribution: this.params.minContribution,
      foveationStrength: this.params.foveationStrength,
      foveationCenter: this.params.foveationCenter,
      antiAlias: this.params.antiAlias,
      fisheye: this.params.fisheye,
      material: this.params.material,
      materialRevision: this.params.material.revision,
      materialTemplate: this.params.materialTemplate,
      colorRamp: this.params.colorRamp,
      colorRampIntensity: this.params.colorRampIntensity,
      useFog: this.params.useFog,
      varyings: this.params.varyings,
      width: frame?.viewRect.width,
      height: frame?.viewRect.height,
      pixelRatio: frame?.graphics.surface.pixelRatio ?? 1,
    })
  }

  fireFrameReady(input: { readonly ready: boolean; readonly loadingCount: number }): void {
    if (!input.ready || input.loadingCount > 0) {
      this.onVisualActivity('gsplat.streaming', this.viewId)
    }
  }
}

class UnifiedGSplatManager extends GSplatManager {
  constructor(
    factory: GSplatRendererFactory,
    initialRenderer: 'cpu' | 'gpu',
    readonly viewId: ViewId,
    readonly viewRuntime: UnifiedViewFrameRuntime,
    private readonly onDestroyed: (manager: UnifiedGSplatManager) => void
  ) {
    super(factory, initialRenderer)
  }

  override destroy(): void {
    try {
      super.destroy()
    } finally {
      this.onDestroyed(this)
    }
  }
}

class UnifiedManagerFactory
  implements GSplatDirectorManagerFactory<GSplatPlacement, GSplatDrawObjectLeaf>
{
  readonly managers = new Set<UnifiedGSplatManager>()

  constructor(
    private readonly adapter: UnifiedGSplatGraphicsAdapter,
    private readonly workBufferPassFactory: GSplatWorkBufferPassFactory,
    private readonly params: GSplatParams,
    private readonly readLodConfig: () => LODConfig,
    private readonly readFrame: (viewId: ViewId) => ViewFrameContext | undefined,
    private readonly logicalFrameSource: object,
    private readonly scene: THREE.Scene,
    private readonly drawEntries: Set<DrawEntry>,
    private readonly vertexBuffer: GSplatBufferLeaf,
    private readonly indexBuffer: GSplatBufferLeaf,
    private readonly readExposure: () => number | undefined,
    private readonly readSceneFog: () => unknown,
    private readonly createHybridPipelineFactory:
      | ((scratch: GSplatHybridRendererScratch) => GSplatHybridPipelineFactory)
      | null,
    private readonly gpuPickIdMode: 'direct' | 'buffer',
    private readonly onVisualActivity: (reason: string, viewId?: ViewId) => void,
    private readonly readShaderEffectOwner: () => AssetId | null
  ) {}

  create(
    input: Parameters<
      GSplatDirectorManagerFactory<GSplatPlacement, GSplatDrawObjectLeaf>['create']
    >[0]
  ): UnifiedGSplatManager {
    const runtime = new UnifiedViewFrameRuntime(
      input.camera,
      this.readFrame(input.viewId),
      input.viewId,
      this.params,
      this.logicalFrameSource,
      this.readExposure,
      this.readSceneFog,
      this.onVisualActivity,
      this.readShaderEffectOwner
    )
    const rendererFactory = this.createRendererFactory(input.viewId, input.layer, runtime)
    const manager = new UnifiedGSplatManager(
      rendererFactory,
      this.params.currentRenderer,
      input.viewId,
      runtime,
      (value) => this.managers.delete(value)
    )
    this.managers.add(manager)
    return manager
  }

  updateViewFrame(viewId: ViewId, frame: ViewFrameContext): void {
    for (const manager of this.managers) {
      if (manager.viewId !== viewId) continue
      manager.viewRuntime.updateFrame(frame)
      manager.invalidateFrameUpdate()
    }
  }

  advanceFrame(frame: EngineFrameContext): void {
    for (const manager of this.managers) manager.viewRuntime.advanceFrame(frame)
  }

  private createRendererFactory(
    viewId: ViewId,
    layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
    runtime: UnifiedViewFrameRuntime
  ): GSplatRendererFactory {
    const adapter = this.adapter
    return {
      graphicsAdapter: adapter,
      frameRuntime: runtime,
      createWorld: () => {
        const lodConfig = this.readLodConfig()
        return new GSplatWorld(
          adapter,
          this.params.format,
          0,
          this.workBufferPassFactory,
          this.params.colorUpdateAngle,
          {
            readLodConfig: () => ({
              lodUpdateDistance: this.params.lodUpdateDistance,
              lodUpdateAngle: this.params.lodUpdateAngle,
              lodBehindPenalty: this.params.lodBehindPenalty,
              lodUnderfillLimit: this.params.lodUnderfillLimit,
              lodRefinementMode: this.params.lodRefinementMode,
              splatBudget: this.params.splatBudget || lodConfig.maxCachePoints,
            }),
            streamingInvalidation: {
              invalidateWorld: (reason) => this.onVisualActivity(`gsplat.${reason}`, viewId),
              requestFrame: (reason) => this.onVisualActivity(`gsplat.${reason}`, viewId),
            },
          }
        )
      },
      createQuadRenderer: (world) => this.createQuadRenderer(viewId, layer, runtime, world),
      createUnifiedSorter: () => {
        const sorter = new GSplatUnifiedSorter({
          interactiveSortScheduler: this.readLodConfig().interactiveSortScheduler,
        })
        sorter.setOnPendingSortedCallback(() => this.onVisualActivity('gsplat.sortReady', viewId))
        sorter.setOnSortJobSettledCallback(() =>
          this.onVisualActivity('gsplat.sortSettled', viewId)
        )
        return sorter
      },
      createHybridScratch: () => {
        if (adapter.backend !== 'webgpu') {
          throw new UnsupportedGraphicsCapabilityError('gsplat.gpu-sort', adapter.backend)
        }
        return new GSplatHybridRendererScratch(adapter)
      },
      createHybridRenderer: (scratch, world) =>
        this.createHybridRenderer(viewId, layer, runtime, scratch, world),
      createShadowRenderer: () => {
        throw new UnsupportedGraphicsCapabilityError('gsplat.hybrid-shadow', adapter.backend)
      },
      subscribeDeviceRestored: () => ({ dispose: () => undefined }),
    }
  }

  private createQuadRenderer(
    viewId: ViewId,
    layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
    runtime: UnifiedViewFrameRuntime,
    world: GSplatManagerWorld
  ): GSplatRenderer {
    let material: GSplatMaterialLeaf | null = null
    let draw: GSplatDrawObjectLeaf | null = null
    try {
      material = this.adapter.createMaterial({
        label: `gsplat-unified-quad:${viewId}:${layer.id}`,
        language: this.adapter.language,
        vertexSource: '#include "gsplatVS"',
        fragmentSource: '#include "gsplatPS"',
      })
      draw = this.adapter.createDrawObject({
        label: `gsplat-unified-quad:${viewId}:${layer.id}`,
        material,
        vertexBuffer: this.vertexBuffer,
        indexBuffer: this.indexBuffer,
      })
      this.scene.add(this.adapter.getDrawObject(draw))
      this.drawEntries.add({ viewId, draw })
      return new GSplatQuadRenderer({
        adapter: this.adapter,
        node: new THREE.Object3D(),
        cameraNode: runtime.camera,
        layer: layer as unknown as Layer<object, GSplatDrawObjectLeaf>,
        workBuffer: world.workBuffer,
        material,
        drawObject: draw,
      })
    } catch (error) {
      draw?.dispose()
      material?.dispose()
      throw error
    }
  }

  private createHybridRenderer(
    viewId: ViewId,
    layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
    runtime: UnifiedViewFrameRuntime,
    scratch: GSplatHybridRendererScratch,
    world: GSplatManagerWorld
  ): GSplatRenderer {
    if (this.adapter.backend !== 'webgpu') {
      throw new UnsupportedGraphicsCapabilityError('gsplat.gpu-sort', this.adapter.backend)
    }

    let material: GSplatMaterialLeaf | null = null
    let draw: GSplatDrawObjectLeaf | null = null
    const pipelineFactory = this.createHybridPipelineFactory?.(scratch) ?? null
    const resolvedPipelineFactory =
      pipelineFactory && !pipelineFactory.createPickLeaves
        ? {
            ...pipelineFactory,
            createPickLeaves: (renderer: GSplatHybridRenderer) => {
              const cloneDrawObject = this.adapter.cloneDrawObject
              if (!cloneDrawObject) {
                throw new Error('WebGPU GSplat adapter 缺少 pick draw clone factory')
              }
              const pickMaterial = this.adapter.createMaterial({
                label: `gsplat-unified-hybrid-pick:${viewId}:${layer.id}`,
                language: this.adapter.language,
                vertexSource: 'gsplatPick',
                fragmentSource: 'gsplatPick',
              })
              let pickDraw: GSplatDrawObjectLeaf | null = null
              try {
                pickDraw = cloneDrawObject.call(this.adapter, renderer.meshInstance, pickMaterial)
                return { material: pickMaterial, drawObject: pickDraw }
              } catch (error) {
                pickDraw?.dispose()
                pickMaterial.dispose()
                throw error
              }
            },
          }
        : pipelineFactory
    try {
      material = this.adapter.createMaterial({
        label: `gsplat-unified-hybrid:${viewId}:${layer.id}`,
        language: this.adapter.language,
        vertexSource: '#include "gsplatHybridVS"',
        fragmentSource: '#include "gsplatPS"',
      })
      draw = this.adapter.createDrawObject({
        label: `gsplat-unified-hybrid:${viewId}:${layer.id}`,
        material,
        vertexBuffer: this.vertexBuffer,
        indexBuffer: this.indexBuffer,
      })
      this.scene.add(this.adapter.getDrawObject(draw))
      this.drawEntries.add({ viewId, draw })
      return new GSplatHybridRenderer({
        adapter: this.adapter,
        node: new THREE.Object3D(),
        cameraNode: runtime.camera,
        layer: layer as unknown as Layer<object, GSplatDrawObjectLeaf>,
        workBuffer: world.workBuffer,
        material,
        drawObject: draw,
        scratch,
        pipelineFactory: resolvedPipelineFactory,
        gpuPickIdMode: this.gpuPickIdMode,
      })
    } catch (error) {
      draw?.dispose()
      material?.dispose()
      throw error
    }
  }
}

interface CachedView {
  readonly frame: ViewFrameContext
  readonly lifecycle: GSplatViewLifecycleContext
}

export class UnifiedSystemDirector implements GSplatSystemDirector {
  private readonly cachedViews = new Map<ViewId, CachedView>()
  private renderableViewIds: ReadonlySet<ViewId> | null = null
  private lastDirectorToken: number | null = null
  private currentDirectorToken: number | null = null
  private currentDirectorFrameNumber: number | null = null
  private currentDirectorManual = false
  private disposed = false

  constructor(
    private readonly director: GSplatDirector<GSplatPlacement, GSplatDrawObjectLeaf>,
    private readonly layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
    private readonly managerFactory: UnifiedManagerFactory
  ) {}

  updateStreaming(frame: EngineFrameContext): void {
    this.assertActive()
    const directorToken = this.resolveDirectorToken(frame)
    this.renderableViewIds = new Set(
      frame.views
        .filter((view) => view.visible && view.rect.width > 0 && view.rect.height > 0)
        .map((view) => view.id)
    )
    this.managerFactory.advanceFrame(frame)
    // Update phase only advances streaming. The full topology/manager update belongs to the
    // per-View Prepare phase, once the current ViewFrameContext (camera and viewport) is known.
    // Calling director.update() here would run the expensive bake/projector/radix/indirect path
    // once before the per-View Prepare phase and duplicate the work for the same logical frame.
    this.director.updateStreaming(directorToken)
  }

  prepareView(
    frame: ViewFrameContext,
    _assets: readonly GSplatSystemAssetSnapshot[],
    lifecycle: GSplatViewLifecycleContext
  ): void {
    this.assertActive()
    this.cachedViews.set(frame.viewId, { frame, lifecycle })
    this.managerFactory.updateViewFrame(frame.viewId, frame)
    this.director.update(
      this.buildViews(),
      this.resolveDirectorToken(frame),
      new Set([frame.viewId])
    )
  }

  private resolveDirectorToken(
    frame: Pick<EngineFrameContext, 'frameNumber' | 'manualFrame'>
  ): number {
    const manual = frame.manualFrame?.deterministic === true
    if (
      this.currentDirectorFrameNumber === frame.frameNumber &&
      this.currentDirectorToken !== null &&
      this.currentDirectorManual === manual
    ) {
      return this.currentDirectorToken
    }
    const token =
      this.lastDirectorToken === null
        ? frame.frameNumber
        : Math.max(frame.frameNumber, this.lastDirectorToken + 1)
    this.lastDirectorToken = token
    this.currentDirectorToken = token
    this.currentDirectorFrameNumber = frame.frameNumber
    this.currentDirectorManual = manual
    return token
  }

  suspendView(viewId: ViewId): void {
    this.cachedViews.delete(viewId)
  }

  retireView(viewId: ViewId, _generation: number, _reason: GSplatViewRetireReason): void {
    this.cachedViews.delete(viewId)
  }

  getMaterial(viewId: ViewId, layerId: number): unknown | null {
    if (layerId !== this.layer.id) return null
    return this.director.getLayerData(viewId, this.layer)?.gsplatManager?.material ?? null
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.cachedViews.clear()
    this.director.destroy()
  }

  private buildViews(): readonly GSplatDirectorView<GSplatPlacement, GSplatDrawObjectLeaf>[] {
    const views: GSplatDirectorView<GSplatPlacement, GSplatDrawObjectLeaf>[] = []
    for (const [viewId, cached] of this.cachedViews) {
      if (this.renderableViewIds && !this.renderableViewIds.has(viewId)) continue
      views.push({
        viewId,
        camera: cached.frame.camera,
        cameraRevision: cached.lifecycle.generation,
        layers: [this.layer],
      })
    }
    return views
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('Unified GSplatSystemDirector 已销毁')
  }
}

/**
 * Shared Unified scene submission used by both WebGL2 and WebGPU.
 *
 * The backend owns the active View viewport/scissor. This leaf only filters Manager-owned raster
 * leaves by ViewId and invokes the backend-specific surface submitter exactly once for the current
 * View, so adjacent Views cannot draw each other's entries.
 */
export class UnifiedRenderSubmission implements GSplatSystemRenderSubmission {
  constructor(
    private readonly adapter: UnifiedGSplatGraphicsAdapter,
    private readonly scene: THREE.Scene,
    private readonly drawEntries: Set<DrawEntry>,
    private readonly vertexBuffer: GSplatBufferLeaf,
    private readonly indexBuffer: GSplatBufferLeaf,
    private readonly renderWebGPUScene?: (scene: THREE.Scene, camera: THREE.Camera) => void
  ) {}

  render(
    frame: ViewFrameContext,
    context: PhaseRenderContext,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): void {
    const hasVisibleAssets = assets.some(({ mounted, handle }) => {
      if (!mounted || !handle.visible) return false
      const object3D = handle.object3D
      return !object3D || (object3D.layers.mask & frame.camera.layers.mask) !== 0
    })
    // 私有 GSplat scene 只是透明叠加层；当前 View 没有目标资产时无需提交空 scene。
    // 跳过提交也避免把某一 View 的临时隐藏状态持久写回共享 draw leaf。
    if (!hasVisibleAssets) return
    const visibility: Array<readonly [THREE.Mesh, boolean]> = []
    for (const entry of [...this.drawEntries]) {
      const mesh = this.adapter.getDrawObject(entry.draw)
      if (mesh.parent !== this.scene) {
        this.drawEntries.delete(entry)
        continue
      }
      visibility.push([mesh, mesh.visible])
      if (entry.viewId === frame.viewId) {
        // Unified 的 raster leaf 位于私有 scene，不是宿主资产 object3D 的子节点，因此
        // 不会继承宿主按 View 配置的 layer mask。每个 View 拥有独立 draw entry；在
        // 提交该 View 时同步相机 mask，避免非默认层相机把已经按 ViewId 选中的高斯
        // 再次通过 THREE.Layers 剔除。
        mesh.layers.mask = frame.camera.layers.mask
      }
      mesh.visible = entry.viewId === frame.viewId && entry.draw.isVisibleForCamera(frame.camera)
    }
    if (visibility.length === 0) return

    try {
      if (context.graphics.kind === 'webgpu') {
        if (!this.renderWebGPUScene) throw new Error('WebGPU Unified GSplat 缺少 surface submitter')
        this.renderWebGPUScene(this.scene, frame.camera)
        return
      }
      const renderer = context.renderer
      const savedTarget = renderer.getRenderTarget()
      const savedViewport = renderer.getViewport(new THREE.Vector4())
      const savedScissor = renderer.getScissor(new THREE.Vector4())
      const savedScissorTest = renderer.getScissorTest()
      const savedAutoClear = renderer.autoClear
      renderer.setRenderTarget(context.outputTarget)
      renderer.setViewport(
        context.viewport.x,
        context.viewport.y,
        context.viewport.width,
        context.viewport.height
      )
      renderer.setScissor(
        context.viewport.x,
        context.viewport.y,
        context.viewport.width,
        context.viewport.height
      )
      renderer.setScissorTest(true)
      renderer.autoClear = false
      try {
        renderer.render(this.scene, frame.camera)
      } finally {
        renderer.autoClear = savedAutoClear
        renderer.setViewport(savedViewport)
        renderer.setScissor(savedScissor)
        renderer.setScissorTest(savedScissorTest)
        renderer.setRenderTarget(savedTarget)
      }
    } finally {
      for (const [mesh, visible] of visibility) mesh.visible = visible
    }
  }

  dispose(): void {
    this.scene.clear()
    this.drawEntries.clear()
    this.indexBuffer.dispose()
    this.vertexBuffer.dispose()
  }
}

class CpuFallbackGpuPickCoordinator implements GSplatGpuPickCoordinator {
  constructor(private readonly cpuPicker: SplatDataCpuPickerContract) {}

  request(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<GSplatPickSample | null> {
    if (query.gpuOnly) return Promise.resolve(null)
    return Promise.resolve(this.cpuPicker.pick(query, assets))
  }

  prepareView(): void {}
  cancelAsset(): void {}
  cancelView(): void {}
  dispose(): void {}
}

interface WebGLPickMaterialEntry {
  readonly source: THREE.RawShaderMaterial
  readonly material: THREE.RawShaderMaterial
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.RawShaderMaterial>
  sourceVersion: number
}

/** WebGL Unified 兼容路径：复用 Quad order/WorkBuffer，只增加私有 ID raster。 */
class WebGLUnifiedGpuPickCoordinator implements GSplatGpuPickCoordinator {
  private readonly scene = new THREE.Scene()
  private readonly materialEntries = new Map<GSplatManager, WebGLPickMaterialEntry>()
  private target: GSplatPickTargetLease | null = null
  private targetWidth = 0
  private targetHeight = 0
  private disposed = false

  constructor(
    private readonly adapter: WebGLGSplatGraphicsAdapter,
    private readonly director: GSplatDirector<GSplatPlacement, GSplatDrawObjectLeaf>,
    private readonly layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
    private readonly placementAssets: ReadonlyMap<number, PlacementAssetEntry>,
    private readonly viewFrames: ReadonlyMap<ViewId, ViewFrameContext>
  ) {
    this.scene.name = 'GSplatUnifiedPickScene'
    this.scene.matrixWorldAutoUpdate = false
  }

  pickSync(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): GSplatPickSample | null {
    this.assertActive()
    if (query.kinds && !query.kinds.includes('gaussian-splat')) return null
    try {
      return this.pickSyncInternal(query, assets)
    } catch (error) {
      log.warn(
        `WebGL Unified GPU pick 失败，回退 CPU: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
      return null
    }
  }

  request(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<GSplatPickSample | null> {
    return Promise.resolve(this.pickSync(query, assets))
  }

  pickRectSync(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly GSplatPickSample[] {
    this.assertActive()
    if (query.kinds && !query.kinds.includes('gaussian-splat')) return []
    try {
      return this.pickRectSyncInternal(query, assets)
    } catch (error) {
      log.warn(
        `WebGL Unified GPU rect pick 失败，返回空结果: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
      return []
    }
  }

  requestRect(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<readonly GSplatPickSample[]> {
    return Promise.resolve(this.pickRectSync(query, assets))
  }

  prepareView(): void {}

  cancelAsset(): void {}

  cancelView(): void {
    this.clearMaterialEntries()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.clearMaterialEntries()
    this.target?.dispose()
    this.target = null
    this.scene.clear()
  }

  private pickSyncInternal(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): GSplatPickSample | null {
    const manager = this.resolveManager(query)
    if (!manager || !(manager.renderer instanceof GSplatQuadRenderer)) return null
    const state = manager.world.getState(manager.world.currentVersion)
    if (!state?.sortedBefore || state.totalActiveSplats === 0) return null
    const draw = manager.renderer.meshInstance
    if (!draw.visible || draw.instancingCount <= 0) return null

    const dimensions = this.resolveTargetDimensions(query)
    const target = this.ensureTarget(dimensions.width, dimensions.height)
    const entry = this.ensureMaterialEntry(manager, manager.renderer)
    this.syncPickMaterial(entry, dimensions.width, dimensions.height)
    const sourceMesh = this.adapter.getDrawObject(draw)
    sourceMesh.updateMatrixWorld(true)
    entry.mesh.matrix.copy(sourceMesh.matrixWorld)
    entry.mesh.matrixWorld.copy(sourceMesh.matrixWorld)
    entry.mesh.layers.mask = query.layerMask ?? query.camera.layers.mask
    entry.mesh.visible = true

    this.adapter.renderPrivatePickScene(target, this.scene, query.camera)
    const region = resolvePickReadRegion(query, dimensions.width, dimensions.height)
    const pixels = this.adapter.readPrivatePickTargetSync(target, region)
    return this.selectPickSample(query, assets, state, pixels, region)
  }

  private pickRectSyncInternal(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly GSplatPickSample[] {
    const pointQuery = createRectCenterQuery(query)
    const manager = this.resolveManager(pointQuery)
    if (!manager || !(manager.renderer instanceof GSplatQuadRenderer)) return []
    const state = manager.world.getState(manager.world.currentVersion)
    if (!state?.sortedBefore || state.totalActiveSplats === 0) return []
    const draw = manager.renderer.meshInstance
    if (!draw.visible || draw.instancingCount <= 0) return []

    const dimensions = this.resolveTargetDimensions(pointQuery)
    const target = this.ensureTarget(dimensions.width, dimensions.height)
    const entry = this.ensureMaterialEntry(manager, manager.renderer)
    this.syncPickMaterial(entry, dimensions.width, dimensions.height)
    const sourceMesh = this.adapter.getDrawObject(draw)
    sourceMesh.updateMatrixWorld(true)
    entry.mesh.matrix.copy(sourceMesh.matrixWorld)
    entry.mesh.matrixWorld.copy(sourceMesh.matrixWorld)
    entry.mesh.layers.mask = query.layerMask ?? query.camera.layers.mask
    entry.mesh.visible = true

    const region = resolvePickRectReadRegion(query, dimensions.width, dimensions.height)
    if (region.width === 0 || region.height === 0) {
      entry.mesh.visible = false
      return []
    }
    this.adapter.renderPrivatePickScene(target, this.scene, query.camera)
    try {
      const pixels = this.adapter.readPrivatePickTargetSync(target, region)
      return this.selectPickSamples(query, assets, state, pixels, region)
    } finally {
      entry.mesh.visible = false
    }
  }

  private resolveManager(query: PickQuery): GSplatManager | null {
    if (query.viewId) {
      const manager = this.director.getLayerData(query.viewId, this.layer)?.gsplatManager
      return manager instanceof GSplatManager ? manager : null
    }
    return (
      this.director
        .getManagers()
        .find(
          (manager): manager is GSplatManager =>
            manager instanceof GSplatManager && manager.renderer.cameraNode === query.camera
        ) ?? null
    )
  }

  private resolveTargetDimensions(query: PickQuery): { width: number; height: number } {
    const frame = query.viewId ? this.viewFrames.get(query.viewId) : undefined
    const rect = query.viewRect ?? query.viewport ?? frame?.viewRect
    const width = Math.max(1, Math.ceil(rect?.width ?? 512))
    const height = Math.max(1, Math.ceil(rect?.height ?? 512))
    const limit = this.adapter.limits.maxTextureDimension2D
    if (width <= limit && height <= limit) return { width, height }
    const scale = Math.min(limit / width, limit / height)
    return {
      width: Math.max(1, Math.floor(width * scale)),
      height: Math.max(1, Math.floor(height * scale)),
    }
  }

  private ensureTarget(width: number, height: number): GSplatPickTargetLease {
    if (this.target && this.targetWidth === width && this.targetHeight === height) {
      return this.target
    }
    const staged = this.adapter.createPrivatePickTarget({
      width,
      height,
      format: 'rgba8unorm',
      withDepth: true,
    })
    const previous = this.target
    this.target = staged
    this.targetWidth = width
    this.targetHeight = height
    previous?.dispose()
    return staged
  }

  private ensureMaterialEntry(
    manager: GSplatManager,
    renderer: GSplatQuadRenderer
  ): WebGLPickMaterialEntry {
    const source = this.adapter.getMaterialObject(renderer.material)
    const current = this.materialEntries.get(manager)
    if (current && current.source === source && current.sourceVersion === source.version) {
      return current
    }
    if (current) this.disposeMaterialEntry(current)

    const material = createWebGLUnifiedPickMaterial(source)
    const sourceMesh = this.adapter.getDrawObject(renderer.meshInstance)
    const mesh = new THREE.Mesh(sourceMesh.geometry, material)
    mesh.name = 'GSplatUnifiedPickMesh'
    mesh.matrixAutoUpdate = false
    mesh.frustumCulled = false
    this.scene.add(mesh)
    const entry = { source, material, mesh, sourceVersion: source.version }
    this.materialEntries.set(manager, entry)
    return entry
  }

  private syncPickMaterial(entry: WebGLPickMaterialEntry, width: number, height: number): void {
    for (const name of Object.keys(entry.material.uniforms)) {
      if (name !== 'pickId' && !entry.source.uniforms[name]) delete entry.material.uniforms[name]
    }
    for (const [name, uniform] of Object.entries(entry.source.uniforms)) {
      entry.material.uniforms[name] = { value: uniform.value }
    }
    entry.material.uniforms.pickId = { value: new THREE.Vector4(0, 0, 0, 0) }
    entry.material.uniforms.viewport = { value: new THREE.Vector2(width, height) }
    entry.material.uniforms.viewport_size = {
      value: new THREE.Vector4(width, height, 1 / width, 1 / height),
    }
    entry.material.uniformsNeedUpdate = true
  }

  private selectPickSample(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[],
    state: GSplatWorldState,
    pixels: Uint8Array,
    region: ReturnType<typeof resolvePickReadRegion>
  ): GSplatPickSample | null {
    const assetSnapshots = new Map(assets.map((asset, order) => [asset.id, { asset, order }]))
    return selectPickSampleFromPixels(pixels, region, (workBufferId) =>
      resolveWorkBufferPickSample(query, state, workBufferId, this.placementAssets, assetSnapshots)
    )
  }

  private selectPickSamples(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[],
    state: GSplatWorldState,
    pixels: Uint8Array,
    region: ReturnType<typeof resolvePickRectReadRegion>
  ): readonly GSplatPickSample[] {
    const pointQuery = createRectCenterQuery(query)
    const assetSnapshots = new Map(assets.map((asset, order) => [asset.id, { asset, order }]))
    return selectPickSamplesFromPixels(
      pixels,
      region,
      (workBufferId) =>
        resolveWorkBufferPickSample(
          pointQuery,
          state,
          workBufferId,
          this.placementAssets,
          assetSnapshots
        ),
      query.maxResults
    )
  }

  private clearMaterialEntries(): void {
    for (const entry of this.materialEntries.values()) this.disposeMaterialEntry(entry)
    this.materialEntries.clear()
  }

  private disposeMaterialEntry(entry: WebGLPickMaterialEntry): void {
    entry.mesh.removeFromParent()
    entry.material.dispose()
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('WebGLUnifiedGpuPickCoordinator 已销毁')
  }
}

/**
 * Pick material 复用 forward shader 与 device-local uniform value，但不调用 Material.clone()。
 * Three 的 cloneUniforms 会把 RenderTarget texture 置空并打印警告，而这些 WorkBuffer attachment
 * 本来就必须由 pick pass 借用同一 GPU 资源。
 */
export function createWebGLUnifiedPickMaterial(
  source: THREE.RawShaderMaterial
): THREE.RawShaderMaterial {
  const uniforms = Object.fromEntries(
    Object.entries(source.uniforms).map(([name, uniform]) => [name, { ...uniform }])
  )
  uniforms.pickId = { value: new THREE.Vector4(0, 0, 0, 0) }
  const material = new THREE.RawShaderMaterial({
    name: `${source.name || 'gsplat-unified'}-pick`,
    vertexShader: source.vertexShader,
    fragmentShader: source.fragmentShader,
    uniforms,
    defines: { ...source.defines, PICK_PASS: '' },
    extensions: { ...source.extensions },
    glslVersion: source.glslVersion,
    side: source.side,
    fog: source.fog,
    lights: source.lights,
    clipping: source.clipping,
    clippingPlanes: source.clippingPlanes,
    clipIntersection: source.clipIntersection,
    clipShadows: source.clipShadows,
    transparent: false,
    blending: THREE.NoBlending,
    depthTest: true,
    depthWrite: false,
    toneMapped: false,
  })
  material.defaultAttributeValues = { ...source.defaultAttributeValues }
  material.index0AttributeName = source.index0AttributeName
  return material
}

export interface WebGLPickAssetSnapshot {
  readonly asset: GSplatSystemAssetSnapshot
  readonly order: number
}

export interface WebGLResolvedPickSample {
  readonly sample: GSplatPickSample
  readonly assetOrder: number
  readonly originalSplatIndex: number
}

export interface PickReadRegion {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly centerX: number
  readonly centerY: number
}

/**
 * Decode one point-pick readback window without any renderer/backend state.
 *
 * The GPU raster can cover the same splat in multiple pixels (and can emit the
 * clear value for untouched pixels).  Keep only one candidate per ID, then use
 * pixel distance followed by asset/order identity as a deterministic tie-break.
 * This is deliberately exported so the contract can be tested independently
 * from WebGPU/WebGL resource setup.
 */
export function selectPickSampleFromPixels(
  pixels: Uint8Array,
  region: PickReadRegion,
  resolve: (pickRecordId: number) => WebGLResolvedPickSample | null
): GSplatPickSample | null {
  const expectedBytes = region.width * region.height * 4
  if (pixels.byteLength < expectedBytes) {
    throw new Error(`GSplat pick readback 数据不足: ${pixels.byteLength} < ${expectedBytes}`)
  }
  const seenIds = new Set<number>()
  let best: (WebGLResolvedPickSample & { readonly pixelDistanceSq: number }) | null = null
  const centerX = region.centerX - region.x
  const centerY = region.centerY - region.y
  for (let y = 0; y < region.height; y++) {
    for (let x = 0; x < region.width; x++) {
      const offset = (y * region.width + x) * 4
      const pickRecordId = GSplatPickIndex.decodeLittleEndian(pixels, offset)
      if (pickRecordId === GSPLAT_PICK_CLEAR_ID || seenIds.has(pickRecordId)) continue
      seenIds.add(pickRecordId)
      const resolved = resolve(pickRecordId)
      if (!resolved) continue
      const pixelDistanceSq = (x - centerX) ** 2 + (y - centerY) ** 2
      if (
        !best ||
        pixelDistanceSq < best.pixelDistanceSq ||
        (pixelDistanceSq === best.pixelDistanceSq &&
          (resolved.assetOrder < best.assetOrder ||
            (resolved.assetOrder === best.assetOrder &&
              resolved.originalSplatIndex < best.originalSplatIndex)))
      ) {
        best = { ...resolved, pixelDistanceSq }
      }
    }
  }
  return best?.sample ?? null
}

export function selectPickSamplesFromPixels(
  pixels: Uint8Array,
  region: PickReadRegion,
  resolve: (pickRecordId: number) => WebGLResolvedPickSample | null,
  maxResults?: number
): readonly GSplatPickSample[] {
  const expectedBytes = region.width * region.height * 4
  if (pixels.byteLength < expectedBytes) {
    throw new Error(`GSplat pick readback 数据不足: ${pixels.byteLength} < ${expectedBytes}`)
  }
  const seenIds = new Set<number>()
  const candidates: Array<WebGLResolvedPickSample & { readonly pixelDistanceSq: number }> = []
  const centerX = region.centerX - region.x
  const centerY = region.centerY - region.y
  for (let y = 0; y < region.height; y++) {
    for (let x = 0; x < region.width; x++) {
      const pickRecordId = GSplatPickIndex.decodeLittleEndian(pixels, (y * region.width + x) * 4)
      if (pickRecordId === GSPLAT_PICK_CLEAR_ID || seenIds.has(pickRecordId)) continue
      seenIds.add(pickRecordId)
      const resolved = resolve(pickRecordId)
      if (!resolved) continue
      candidates.push({
        ...resolved,
        pixelDistanceSq: (x - centerX) ** 2 + (y - centerY) ** 2,
      })
    }
  }
  candidates.sort(
    (a, b) =>
      (a.sample.distance ?? Number.POSITIVE_INFINITY) -
        (b.sample.distance ?? Number.POSITIVE_INFINITY) ||
      a.pixelDistanceSq - b.pixelDistanceSq ||
      a.assetOrder - b.assetOrder ||
      a.originalSplatIndex - b.originalSplatIndex
  )
  const limit =
    maxResults === undefined
      ? candidates.length
      : Number.isSafeInteger(maxResults) && maxResults >= 0
        ? maxResults
        : 0
  return candidates.slice(0, limit).map(({ sample }) => sample)
}

function createRectCenterQuery(query: RectPickQuery): PickQuery {
  const ndc = new THREE.Vector2(
    (query.min.x + query.max.x) * 0.5,
    (query.min.y + query.max.y) * 0.5
  )
  return {
    ndc,
    ray: ndcToRay(ndc, query.camera),
    camera: query.camera,
    viewport: query.viewport ?? query.viewRect,
    viewId: query.viewId,
    viewRect: query.viewRect,
    layerMask: query.layerMask,
    kinds: query.kinds,
    interactionType: 'click',
    signal: query.signal,
  }
}

export function resolvePickReadRegion(
  query: PickQuery,
  width: number,
  height: number,
  origin: 'bottom-left' | 'top-left' = 'bottom-left'
): PickReadRegion {
  // Match PlayCanvas Picker's quarter-resolution contract: callers scale the CSS coordinate by
  // pickerWidth / viewWidth and the picker sanitizes it with floor().  Using (width - 1) plus
  // round() shifts a reduced target up to one picker pixel (four full-resolution pixels at the
  // default 0.25 scale), which is visible as a consistent pick-point offset.
  const centerX = Math.max(0, Math.min(width - 1, Math.floor((query.ndc.x * 0.5 + 0.5) * width)))
  const normalizedY = query.ndc.y * 0.5 + 0.5
  const readY = origin === 'top-left' ? 1 - normalizedY : normalizedY
  const centerY = Math.max(0, Math.min(height - 1, Math.floor(readY * height)))
  const defaultWindow =
    query.interactionType === 'click' ? 5 : query.interactionType === 'move' ? 3 : 1
  const requestedWindowSize = query.windowSize
  const windowSize = Number.isFinite(requestedWindowSize)
    ? Math.max(1, Math.floor(requestedWindowSize as number))
    : defaultWindow
  const half = Math.floor(windowSize / 2)
  const x = Math.max(0, centerX - half)
  const y = Math.max(0, centerY - half)
  return {
    x,
    y,
    width: Math.max(1, Math.min(width, centerX + half + 1) - x),
    height: Math.max(1, Math.min(height, centerY + half + 1) - y),
    centerX,
    centerY,
  }
}

/**
 * 将矩形 NDC（左下 min、右上 max）转换为私有 target 的读回区域。
 * WebGL/WebGPU 的 origin 差异由 origin 参数处理；返回的区域始终是连续像素块，
 * 并在边缘做一次 clamp，避免 readback 越界。
 */
export function resolvePickRectReadRegion(
  query: { readonly min: THREE.Vector2; readonly max: THREE.Vector2 },
  width: number,
  height: number,
  origin: 'bottom-left' | 'top-left' = 'bottom-left'
): PickReadRegion {
  const minX = Math.min(query.min.x, query.max.x)
  const maxX = Math.max(query.min.x, query.max.x)
  const minY = Math.min(query.min.y, query.max.y)
  const maxY = Math.max(query.min.y, query.max.y)
  const clippedMinX = Math.max(-1, minX)
  const clippedMaxX = Math.min(1, maxX)
  const clippedMinY = Math.max(-1, minY)
  const clippedMaxY = Math.min(1, maxY)
  if (clippedMaxX < clippedMinX || clippedMaxY < clippedMinY) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      centerX: 0,
      centerY: 0,
    }
  }
  const left = Math.max(0, Math.min(width - 1, Math.floor((clippedMinX * 0.5 + 0.5) * width)))
  const right = Math.max(left + 1, Math.min(width, Math.ceil((clippedMaxX * 0.5 + 0.5) * width)))
  const minNormalizedY = clippedMinY * 0.5 + 0.5
  const maxNormalizedY = clippedMaxY * 0.5 + 0.5
  const low = origin === 'top-left' ? 1 - maxNormalizedY : minNormalizedY
  const high = origin === 'top-left' ? 1 - minNormalizedY : maxNormalizedY
  const top = Math.max(0, Math.min(height - 1, Math.floor(low * height)))
  const bottom = Math.max(top + 1, Math.min(height, Math.ceil(high * height)))
  return {
    x: left,
    y: top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
    centerX: (left + right - 1) * 0.5,
    centerY: (top + bottom - 1) * 0.5,
  }
}

export function resolveWorkBufferPickSample(
  query: PickQuery,
  state: GSplatWorldState,
  workBufferId: number,
  placementAssets: ReadonlyMap<number, PlacementAssetEntry>,
  assets: ReadonlyMap<AssetId, WebGLPickAssetSnapshot>
): WebGLResolvedPickSample | null {
  // Newer WorldState snapshots may expose an immutable pickRecordId index.  When
  // present, use it as the authoritative identity and only fall back to the
  // legacy interval layout for snapshots that predate the index.  This keeps the
  // migration backwards compatible while preventing compact/sorted positions
  // from being mistaken for original splat indices.
  const mappedRecordId = state.pickIndex.getRecordIdForWorkBufferId(workBufferId)
  const indexedRecord =
    mappedRecordId === undefined
      ? state.pickIndex.get(workBufferId)
      : state.pickIndex.get(mappedRecordId)
  for (const splat of state.splats) {
    const placementId = (splat as GSplatWorldSplat & { readonly placementId?: number }).placementId
    if (placementId === undefined) continue
    const placementAsset = placementAssets.get(placementId)
    if (!placementAsset) continue
    const snapshot = assets.get(placementAsset.id)
    if (
      !snapshot ||
      !snapshot.asset.mounted ||
      !snapshot.asset.handle.visible ||
      snapshot.asset.generation !== placementAsset.binding.generation ||
      (query.assetIds && !query.assetIds.includes(placementAsset.id)) ||
      !splat.node.visible ||
      (query.layerMask !== undefined && (splat.node.layers.mask & query.layerMask) === 0)
    ) {
      continue
    }

    if (
      indexedRecord &&
      (indexedRecord.placementId !== placementId ||
        indexedRecord.sourceResourceId !== splat.resource.id)
    ) {
      continue
    }
    const originalSplatIndex = indexedRecord
      ? indexedRecord.originalSplatIndex
      : resolveOriginalSplatIndex(state, splat, workBufferId)
    if (originalSplatIndex === null) continue
    const centers = splat.resource.centers
    const offset = originalSplatIndex * 3
    if (!centers || offset + 2 >= centers.length) continue
    splat.node.updateMatrixWorld(true)
    const point = new THREE.Vector3(
      centers[offset],
      centers[offset + 1],
      centers[offset + 2]
    ).applyMatrix4(splat.node.matrixWorld)
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)) {
      continue
    }
    if (!isPickPointAtOrBeyondMinDistance(query.ray, point, query.minDistance)) {
      continue
    }
    return {
      sample: {
        assetId: placementAsset.id,
        resourceGeneration: placementAsset.binding.generation,
        point,
        distance: query.ray.origin.distanceTo(point),
        raw: Object.freeze({
          splatIndex: originalSplatIndex,
          originalSplatIndex,
          placementId,
          sourceResourceId: splat.resource.id,
          workBufferId,
          ...(indexedRecord
            ? { pickRecordId: mappedRecordId === undefined ? workBufferId : mappedRecordId }
            : {}),
          source: 'gpu',
        }),
      },
      assetOrder: snapshot.order,
      originalSplatIndex,
    }
  }
  return null
}

function resolveOriginalSplatIndex(
  state: GSplatWorldState,
  splat: GSplatWorldSplat,
  workBufferId: number
): number | null {
  const offsets = state.intervalOffsetsBySplat.get(splat)
  if (!offsets || offsets.length === 0) return null
  if (splat.intervals.length === 0) {
    const local = workBufferId - offsets[0]
    return local >= 0 && local < splat.activeSplats ? local : null
  }
  for (let interval = 0; interval < splat.intervals.length; interval += 2) {
    const start = splat.intervals[interval]
    const end = splat.intervals[interval + 1]
    const local = workBufferId - offsets[interval / 2]
    if (local >= 0 && local < end - start) return start + local
  }
  return null
}

class UnifiedHostServices implements GSplatSystemHostServices {
  private disposed = false

  constructor(
    private readonly adapter: GSplatGraphicsAdapter,
    private readonly params: GSplatParams,
    private readonly loaderRegistry: ReturnType<typeof createCanonicalGSplatLoaderRegistry>,
    private readonly managerFactory: UnifiedManagerFactory,
    private readonly readLodConfig: () => LODConfig,
    private readonly updateLodConfig: (config: Partial<LODConfig>) => void,
    private readonly readDiagnostics: () => GSplatDeviceBudgetDiagnostics
  ) {}

  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean {
    if (!this.params.setClipVolumes(volumes)) return false
    for (const manager of this.managerFactory.managers) {
      manager.invalidateFrameUpdate()
    }
    return true
  }

  setLodConfig(config: Partial<LODConfig>): void {
    this.updateLodConfig(config)
  }

  getLodConfig(): LODConfig {
    return { ...this.readLodConfig() }
  }

  getLodStats(): LODStats {
    if (this.managerFactory.managers.size === 0) return EMPTY_LOD_STATS
    let cachedPoints = 0
    let pendingLoads = 0
    let totalGroups = 0
    for (const manager of this.managerFactory.managers) {
      cachedPoints += manager.world.getState(manager.world.currentVersion)?.totalActiveSplats ?? 0
      pendingLoads += manager.world.pendingLoadCount
      if (manager.world.hasOctreeInstances) totalGroups++
    }
    return {
      ...EMPTY_LOD_STATS,
      totalGroups,
      cachedPoints,
      pendingLoads,
      activeLoads: pendingLoads,
    }
  }

  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics {
    return this.readDiagnostics()
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot | undefined {
    const reasons: string[] = []
    const viewIds = new Set<ViewId>()
    for (const manager of this.managerFactory.managers) {
      const activity = manager.getVisualActivitySnapshot()
      if (!activity.needsNextFrame) continue
      reasons.push(...activity.reasons)
      viewIds.add(manager.viewId)
    }
    if (reasons.length === 0) return { needsNextFrame: false, reasons: [] }
    return {
      needsNextFrame: true,
      reasons: [...new Set(reasons)],
      viewIds: [...viewIds],
    }
  }

  ensureExtraStreams(streams: readonly GSplatStreamDescriptor[]): boolean {
    return this.params.addExtraStreamsSelection(streams, [...this.managerFactory.managers])
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.loaderRegistry.dispose()
    this.params.dispose()
    GSplatResourceCleanup.destroyAdapter(this.adapter)
  }
}

/** WebGL 与 WebGPU 共用同一 Unified 对象图；这里只注入 WebGL 图形 leaf。 */
export function createWebGLGSplatUnifiedSystem(
  input: WebGLGSplatUnifiedCompositionInput
): GSplatSystem {
  return createUnifiedGSplatSystem({
    ...input,
    adapter: input.adapter,
    workBufferPassFactory: new WebGLGSplatWorkBufferPassFactory(input.adapter),
    capabilities: createWebGLGSplatCapabilities(input.adapter),
  })
}

export function createUnifiedGSplatSystem(input: UnifiedGSplatCompositionInput): GSplatSystem {
  const { adapter } = input
  const params = new GSplatParams(adapter)
  const composition = new LayerComposition<GSplatPlacement, GSplatDrawObjectLeaf>()
  const layer = new Layer<GSplatPlacement, GSplatDrawObjectLeaf>('gsplat-unified')
  layer.id = 0
  composition.addLayer(layer)
  const scene = new THREE.Scene()
  scene.name = 'GSplatUnifiedScene'
  const drawEntries = new Set<DrawEntry>()
  const geometry = createBatchGeometryBuffers(adapter)
  const viewFrames = new Map<ViewId, ViewFrameContext>()
  const logicalFrameSource = {}
  let activeShaderEffectOwner: AssetId | null = null
  let renderedSplatCount = 0
  let lodConfigRaw: Partial<LODConfig> = { ...(input.options.lodConfig ?? {}) }
  let budgetResult = resolveGSplatDeviceBudgetConfig(
    DEFAULT_LOD_CONFIG,
    lodConfigRaw,
    input.context.getRendererIfAvailable() ?? undefined
  )
  let lodConfig = budgetResult.lodConfig
  applyLodConfigToParams(
    params,
    lodConfig,
    input.initialConfig?.maxSplats ?? DEFAULT_CONFIG.maxSplats
  )

  const managerFactory = new UnifiedManagerFactory(
    adapter,
    input.workBufferPassFactory,
    params,
    () => lodConfig,
    (viewId) => viewFrames.get(viewId),
    logicalFrameSource,
    scene,
    drawEntries,
    geometry.vertexBuffer,
    geometry.indexBuffer,
    () => input.context.getRendererIfAvailable()?.toneMappingExposure,
    () => input.context.sceneGraph.mainScene.fog,
    input.createHybridPipelineFactory ?? null,
    input.options.gpuPickIdMode ?? 'direct',
    (reason, viewId) => input.onVisualActivity(reason, undefined, viewId),
    () => activeShaderEffectOwner
  )
  const shaderEffects = input.capabilities.supported.includes('shader-effects')
    ? new GSplatUnifiedShaderEffectController(
        params.material,
        () =>
          [...managerFactory.managers]
            .map((manager) => manager.renderer.material)
            .filter((material): material is GSplatMaterialLeaf => material !== null),
        input.onVisualActivity,
        (owner) => {
          if (activeShaderEffectOwner === owner) return
          activeShaderEffectOwner = owner
          for (const manager of managerFactory.managers) manager.invalidateFrameUpdate()
        }
      )
    : null
  const director = new GSplatDirector<GSplatPlacement, GSplatDrawObjectLeaf>(managerFactory, {
    onStats: (stats) => {
      renderedSplatCount = stats.gsplatCount
    },
    applyParams: () => params.frameUpdate(),
    processResourceCleanup: () => GSplatResourceCleanup.process(adapter),
    clearParamsDirty: () => {
      params.dirty = false
    },
    requestFrame: (viewIds) => {
      if (viewIds?.length) {
        for (const viewId of viewIds) {
          input.onVisualActivity('gsplat.streaming', undefined, viewId)
        }
        return
      }
      input.onVisualActivity('gsplat.streaming')
    },
  })
  const systemDirector = new UnifiedSystemDirector(director, layer, managerFactory)
  const configuration = new UnifiedGSplatConfiguration(
    input.capabilities,
    params,
    () => [...managerFactory.managers],
    input.onVisualActivity,
    input.initialConfig
  )
  const loaderRegistry = createCanonicalGSplatLoaderRegistry({
    adapter,
    withCredentials: input.options.withCredentials ?? false,
    readMaxSplats: () => configuration.getConfig().maxSplats,
    readLoadSHN: () => lodConfig.loadSHN,
    readMaxConcurrentLoads: () => lodConfig.maxConcurrentLoads,
    // PlayCanvas keeps centers available by default (Scene#gsplatCentersEnabled=true). The
    // unified GPU picker resolves an ID back to a world-space point from the same CPU centers
    // array, so restricting this to CPU sorting makes every WebGPU pick resolve as a miss.
    // Callers may still opt out per load with prepareCenters:false when they do not need picking
    // or CPU export.
    readPrepareCenters: () => true,
  })
  const bindingRuntime = new UnifiedBindingRuntime(composition, input.onVisualActivity)
  let hostServices: UnifiedHostServices | null = null
  const centersDisplayModes = new Map<AssetId, CentersDisplayMode>()
  const bindingFactory = new TrackingBindingFactory({
    adapter,
    composition: composition as unknown as LayerComposition<GSplatPlacement>,
    runtime: bindingRuntime,
    createNode: () => {
      const node = new THREE.Object3D()
      input.context.sceneGraph.gaussianSplatRoot.add(node)
      return node
    },
    createBehavior: ({ id }): GSplatHandleBehavior => {
      const resolvePlacement = (): GSplatPlacement | null => {
        const entries = [...bindingFactory.placementAssets.values()]
        for (let index = entries.length - 1; index >= 0; index--) {
          const entry = entries[index]
          if (entry.id === id && !entry.binding.placement.isDestroyed) {
            return entry.binding.placement
          }
        }
        return null
      }
      const resolveEnvironmentManager = (): UnifiedGSplatManager | null => {
        const placement = resolvePlacement()
        if (!placement) return null
        for (const manager of managerFactory.managers) {
          if (manager.getEnvironmentConfigured?.(placement) !== undefined) return manager
        }
        return null
      }
      // 直方图按 assetId 过滤，避免多 View/多资产时把其它 placement 的样本统计进来。
      const resolveHistogramSource = (): UnifiedScalarHistogramSource | null => {
        for (const manager of managerFactory.managers) {
          const state = manager.world.getState(manager.world.currentVersion)
          if (state?.splats.some((splat) => splat.assetId === id)) {
            return { splats: state.splats, version: state.version }
          }
        }
        return null
      }
      const behavior =
        shaderEffects?.createBehavior(id, () => hostServices?.getLodStats() ?? EMPTY_LOD_STATS) ??
        ({
          getLodStats: () => hostServices?.getLodStats() ?? EMPTY_LOD_STATS,
        } satisfies GSplatHandleBehavior)
      return {
        ...behavior,
        getSortingMode: () => configuration.getConfig().sortingMode,
        getResolvedSortingMode: () => configuration.getResolvedSortingMode(),
        getEnvironmentConfigured: () => {
          const placement = resolvePlacement()
          const manager = placement ? resolveEnvironmentManager() : null
          return manager?.getEnvironmentConfigured?.(placement!) ?? false
        },
        getEnvironmentVisible: () => {
          const placement = resolvePlacement()
          const manager = placement ? resolveEnvironmentManager() : null
          return manager?.getEnvironmentVisible?.(placement!) ?? false
        },
        setEnvironmentVisible: (visible) => {
          const placement = resolvePlacement()
          const manager = placement ? resolveEnvironmentManager() : null
          if (!placement || !manager) return false
          const changed = manager.setEnvironmentVisible?.(placement, visible) ?? false
          if (changed) input.onVisualActivity('gsplat.environment')
          return changed
        },
        ...createUnifiedScalarFilterBehavior(
          id,
          resolvePlacement,
          (placement) => bindingRuntime.invalidatePlacement(placement, 'work-buffer-update'),
          resolveHistogramSource
        ),
        setCentersDisplayMode: (mode) => {
          // 终端工程的 3DGS 加载揭示流程会在 WebGL 路径调用该 API。WebGL unified
          // 当前渲染仍由 WorkBuffer gaussian pass 负责，中心点独立 pass 后续补齐；这里
          // 保存模式并切换 placement node 可见性，避免 centers-only 阶段提前显示高斯。
          centersDisplayModes.set(id, mode)
          const placement = resolvePlacement()
          if (placement) {
            placement.node.visible = mode !== 'centers-only'
            placement.markDirty()
          }
          input.onVisualActivity('gsplat.centersDisplay')
          return true
        },
        setCentersStyle: () => {
          input.onVisualActivity('gsplat.centersStyle')
          return true
        },
        setColorAdjustment: (options) => configuration.setColorAdjustment(options),
        getColorAdjustment: () => configuration.getColorAdjustment(),
        resetColorAdjustment: () => configuration.resetColorAdjustment(),
      }
    },
  })
  const cpuPicker = new SplatDataCpuPicker({
    readActiveSources: (query) =>
      collectActivePickSources(query, director, layer, bindingFactory.placementAssets),
  })
  const gpuPickCoordinator: GSplatGpuPickCoordinator =
    adapter instanceof WebGLGSplatGraphicsAdapter
      ? new WebGLUnifiedGpuPickCoordinator(
          adapter,
          director,
          layer,
          bindingFactory.placementAssets,
          viewFrames
        )
      : (input.createGpuPickCoordinator?.({
          adapter,
          scene,
          director,
          layer,
          placementAssets: bindingFactory.placementAssets,
          viewFrames,
          onVisualActivity: input.onVisualActivity,
        }) ?? new CpuFallbackGpuPickCoordinator(cpuPicker))
  hostServices = new UnifiedHostServices(
    adapter,
    params,
    loaderRegistry,
    managerFactory,
    () => lodConfig,
    (patch) => {
      lodConfigRaw = { ...lodConfigRaw, ...patch }
      budgetResult = resolveGSplatDeviceBudgetConfig(
        DEFAULT_LOD_CONFIG,
        lodConfigRaw,
        input.context.getRendererIfAvailable() ?? undefined
      )
      lodConfig = budgetResult.lodConfig
      applyLodConfigToParams(params, lodConfig, configuration.getConfig().maxSplats)
      input.onVisualActivity('gsplat.lodConfig')
    },
    () => budgetResult.diagnostics
  )

  try {
    return new GSplatSystem({
      adapter,
      bindingFactory,
      director: {
        updateStreaming: (frame) => {
          shaderEffects?.update(frame.delta)
          systemDirector.updateStreaming(frame)
        },
        prepareView: (frame, assets, lifecycle) => {
          viewFrames.set(frame.viewId, frame)
          systemDirector.prepareView(frame, assets, lifecycle)
        },
        suspendView: (viewId) => systemDirector.suspendView(viewId),
        retireView: (viewId, generation, reason) => {
          viewFrames.delete(viewId)
          systemDirector.retireView(viewId, generation, reason)
        },
        getMaterial: (viewId, layerId) => systemDirector.getMaterial(viewId, layerId),
        dispose: () => {
          let firstError: unknown
          try {
            shaderEffects?.dispose()
          } catch (error) {
            firstError = error
          }
          try {
            systemDirector.dispose()
          } catch (error) {
            firstError ??= error
          }
          if (firstError) throw firstError
        },
      },
      configuration,
      cpuPicker,
      gpuPickCoordinator,
      renderSubmission: new UnifiedRenderSubmission(
        adapter,
        scene,
        drawEntries,
        geometry.vertexBuffer,
        geometry.indexBuffer,
        input.renderWebGPUScene
      ),
      loaders: loaderRegistry.loaders,
      exporters: input.exporters,
      runtimeControl: new GSplatParamsRuntimeControl(params, () => renderedSplatCount),
      hostServices,
      onVisualActivity: (reason, viewId) => input.onVisualActivity(reason, undefined, viewId),
    })
  } catch (error) {
    gpuPickCoordinator.dispose()
    cpuPicker.dispose()
    shaderEffects?.dispose()
    systemDirector.dispose()
    hostServices.dispose()
    geometry.indexBuffer.dispose()
    geometry.vertexBuffer.dispose()
    throw error
  }
}

function collectActivePickSources(
  query: PickQuery,
  director: GSplatDirector<GSplatPlacement, GSplatDrawObjectLeaf>,
  layer: Layer<GSplatPlacement, GSplatDrawObjectLeaf>,
  placementAssets: ReadonlyMap<number, PlacementAssetEntry>
) {
  const managers: readonly GSplatManager[] = query.viewId
    ? [director.getLayerData(query.viewId, layer)?.gsplatManager].filter(
        (value): value is GSplatManager => value instanceof GSplatManager
      )
    : director
        .getManagers()
        .filter((value): value is GSplatManager => value instanceof GSplatManager)
  const result: SplatDataCpuPickSource[] = []
  for (const manager of managers) {
    const state = manager.world.getState(manager.world.currentVersion)
    if (!state) continue
    for (const splat of state.splats) {
      const placementId = (splat as GSplatWorldSplat & { readonly placementId?: number })
        .placementId
      if (placementId === undefined) continue
      const asset = placementAssets.get(placementId)
      if (!asset || !splat.resource.centers) continue
      result.push({
        assetId: asset.id,
        resourceGeneration: asset.binding.generation,
        placementId,
        sourceResourceId: splat.resource.id,
        node: splat.node,
        centers: splat.resource.centers,
        intervals: splat.intervals,
        readSplatShape: splat.resource.readSplatShape?.bind(splat.resource),
      })
    }
  }
  return result
}

function createBatchGeometryBuffers(adapter: GSplatGraphicsAdapter): {
  readonly vertexBuffer: GSplatBufferLeaf
  readonly indexBuffer: GSplatBufferLeaf
} {
  const positions = new Float32Array(GSPLAT_BATCH_VERTEX_COUNT * 3)
  const indices = new Uint32Array(GSPLAT_BATCH_INDEX_COUNT)
  for (let index = 0; index < GSPLAT_INSTANCE_SIZE; index++) {
    const vertexBase = index * 4
    positions.set([-1, -1, index, 1, -1, index, 1, 1, index, -1, 1, index], vertexBase * 3)
    indices.set(
      [vertexBase, vertexBase + 1, vertexBase + 2, vertexBase, vertexBase + 2, vertexBase + 3],
      index * 6
    )
  }

  let vertexBuffer: GSplatBufferLeaf | null = null
  let indexBuffer: GSplatBufferLeaf | null = null
  try {
    vertexBuffer = adapter.createBuffer({
      label: 'gsplat-unified-batch-vertices',
      byteLength: positions.byteLength,
      usages: ['vertex', 'copy-dst'],
    })
    indexBuffer = adapter.createBuffer({
      label: 'gsplat-unified-batch-indices',
      byteLength: indices.byteLength,
      usages: ['index', 'copy-dst'],
    })
    adapter.updateBuffer(vertexBuffer, positions)
    adapter.updateBuffer(indexBuffer, indices)
    return { vertexBuffer, indexBuffer }
  } catch (error) {
    indexBuffer?.dispose()
    vertexBuffer?.dispose()
    throw error
  }
}

function applyLodConfigToParams(params: GSplatParams, config: LODConfig, maxSplats: number): void {
  params.radialSorting = config.radialSorting ?? params.radialSorting
  params.lodUpdateDistance = config.lodUpdateDistance ?? params.lodUpdateDistance
  params.lodUpdateAngle = config.lodUpdateAngle ?? params.lodUpdateAngle
  params.lodBehindPenalty = config.lodBehindPenalty ?? params.lodBehindPenalty
  params.lodUnderfillLimit = config.lodUnderfillLimit ?? params.lodUnderfillLimit
  params.lodRefinementMode = config.lodRefinementMode ?? params.lodRefinementMode
  params.splatBudget =
    config.maxCachePoints > 0 ? Math.min(config.maxCachePoints, maxSplats) : maxSplats
}

function validateRenderConfig(config: GaussianSplatRenderConfig): void {
  if (
    config.sortingMode !== 'auto' &&
    config.sortingMode !== 'cpu' &&
    config.sortingMode !== 'gpu'
  ) {
    throw new Error(`不支持的 GSplat sortingMode: ${config.sortingMode}`)
  }
  if (!['low', 'medium', 'high'].includes(config.qualityLevel)) {
    throw new Error(`不支持的 GSplat qualityLevel: ${config.qualityLevel}`)
  }
  if (!Number.isSafeInteger(config.maxSplats) || config.maxSplats < 0) {
    throw new Error(`非法 GSplat maxSplats: ${config.maxSplats}（0 表示不限制）`)
  }
  if (!Number.isFinite(config.opacity) || config.opacity < 0 || config.opacity > 1) {
    throw new Error(`非法 GSplat opacity: ${config.opacity}`)
  }
}

function noopBindingTransaction(): GSplatBindingTransaction {
  return { commit: () => undefined, rollback: () => undefined }
}
